// Ubah respons /series/aggregated jadi bentuk `summary.{type}.data` punya
// endpoint lama, supaya buildFmsSeries dan komponen chart gak perlu diubah
// sama sekali. Sengaja nol import biar bisa dijalanin langsung pakai node.

// `additional` isinya {FUNGSI}_{fieldDB}_{indeks}, mis. AVG_dlrpRpm_0.
// Kalau gak diminta lewat functionNames, backend ngasihnya array kosong.
// Pemanggil selalu minta persis satu fungsi per prefix per panggilan, jadi
// match pertama gak ambigu. Kalau itu berubah, helper perlu field name juga.
function pickAggregated(row, prefix) {
  const additional = row && row.additional;
  if (!additional || Array.isArray(additional)) return undefined;
  const hit = Object.keys(additional).find((key) => key.startsWith(prefix));
  return hit === undefined ? undefined : additional[hit];
}

function groupByTimestamp(rows) {
  const out = {};
  (rows || []).forEach((row) => {
    if (!row || row.timestamp === undefined) return;
    const ts = String(row.timestamp);
    if (!out[ts]) out[ts] = [];
    out[ts].push(row);
  });
  return out;
}

export function seriesToSummaryData(type, rows, labelByUid = {}) {
  const buckets = groupByTimestamp(rows);
  const out = {};
  // Dipakai rpm/ae: aggregate yang diminta tapi gak ada di respons cuma perlu
  // di-warn sekali per panggilan, bukan sekali per baris.
  let warnedMissingAggregate = false;

  Object.keys(buckets).forEach((ts) => {
    const group = buckets[ts];

    if (type === 'rpm') {
      const bucket = {};
      group.forEach((row) => {
        if (!row.type) return;
        const avg = pickAggregated(row, 'AVGGT_');
        if (avg === undefined && !warnedMissingAggregate) {
          console.warn('[rollup chart] rpm: AVGGT_ yang diminta gak ada di respons, fallback ke sampel dasar');
          warnedMissingAggregate = true;
        }
        bucket[row.type] = avg === undefined ? row : { ...row, rpm: avg };
      });
      out[ts] = bucket;
      return;
    }

    if (type === 'ae') {
      const bucket = {};
      group.forEach((row) => {
        // `no` 0-based di respons, chart pakai AE1/AE2.
        const no = Number(row.no);
        if (!Number.isInteger(no) || no < 0) return;
        const key = `AE${no + 1}`;
        const runningSeconds = pickAggregated(row, 'SUM_');
        if (runningSeconds === undefined && !warnedMissingAggregate) {
          console.warn('[rollup chart] ae: SUM_ yang diminta gak ada di respons, runningSeconds jadi undefined');
          warnedMissingAggregate = true;
        }
        // `.rpm` diteruskan apa adanya, gak ada consumer yang baca field ini
        // dari jalur summary. Kalau nanti chart AE rpm butuh, koreksi AVG dulu.
        bucket[key] = { ...row, runningSeconds };
      });
      out[ts] = bucket;
      return;
    }

    if (type === 'flowmeter') {
      const bucket = {};
      group.forEach((row) => {
        const label = labelByUid[row.deviceId];
        if (!label) return;
        bucket[label] = row;
        // RPM/Fuel/Speed baca skalar {NAME}_FuelCons. Nama group = label tanpa
        // suffix _IN/_OUT, samain sama aturan store/asset.js: default-nya IN,
        // cuma _OUT yang dikecualikan. Endpoint lama ngisi skalar ini dengan
        // volumeFlowrate yang sama persis, TAPI itu cuma terverifikasi di aset
        // yang flowmeternya semua _IN. Belum ada aset in/out yang bisa dicek,
        // jadi belum terbukti volumeFlowrate (gross IN) ini masih sama dengan
        // _FuelCons legacy (net) begitu ada line _OUT.
        const groupName = label.replace(/_(IN|OUT)$/, '');
        if (!label.endsWith('_OUT')) {
          bucket[`${groupName}_FuelCons`] = row.volumeFlowrate;
        }
      });
      out[ts] = bucket;
      return;
    }

    // gps (dan tipe lain kalau nanti nambah): satu baris per bucket. Diasumsikan
    // cuma satu device GPS per aset — aset multi-GPS bakal diam-diam cuma nyimpen
    // baris yang datang duluan (group[0]), sisanya kebuang.
    const row = group[0];
    const avg = pickAggregated(row, 'AVG_');
    out[ts] = avg === undefined ? row : { ...row, speed: avg };
  });

  return out;
}

// Endpoint /series/aggregated punya batas keras `limit: 6000` BARIS, dan kalau
// kena dia motong diam-diam: `count` mentok 6000, sisanya hilang tanpa error.
// Terverifikasi: 90 hari flowmeter 3 device @HOUR = 6.480 baris diharapkan,
// yang balik cuma 6.000.
// Baris = bucket x channel, jadi pemecahnya harus dihitung dari anggaran baris,
// bukan dari jumlah hari.
export function buildChunkRanges(start, end, chunkSeconds) {
  if (!(chunkSeconds > 0) || !(end > start)) return [{ start, end }];
  const out = [];
  let cursor = start;
  while (cursor < end) {
    const next = Math.min(cursor + chunkSeconds, end);
    out.push({ start: cursor, end: next });
    cursor = next;
  }
  return out;
}

export default seriesToSummaryData;
